/**
 * BPM Card
 *
 * Displays BPM calculation results:
 * - Gross BPM
 * - Depreciation percentage
 * - Rest BPM
 * - Forfaitaire band label
 * - Warning when BPM is estimated (not from RDW)
 */

import { useTranslation } from 'react-i18next';
import { Receipt, AlertCircle, Info } from 'lucide-react';
import type { BpmResult, RiskFlags } from '../lib/types';
import { formatEuro, formatPct } from '../lib/format';
import { cn } from '@/lib/utils';

interface BpmCardProps {
  bpm: BpmResult;
  riskFlags?: RiskFlags | null;
  className?: string;
}

export function BpmCard({ bpm, riskFlags, className }: BpmCardProps) {
  const { t } = useTranslation('common');

  const hasData = bpm.grossBpm !== null || bpm.restBpm !== null;
  const isElectric = riskFlags?.isElectric ?? false;
  const isEstimated = bpm.grossBpmSource === 'CALCULATED';

  // Determine if we're missing critical data
  const missingData = !hasData && !isElectric;
  const missingReasons: string[] = [];
  if (riskFlags?.missingBpm && riskFlags?.missingCo2) {
    missingReasons.push(t('calculator.missingBpmAndCo2', 'Bruto BPM en CO₂ ontbreken in RDW'));
  } else if (riskFlags?.missingBpm) {
    missingReasons.push(t('calculator.missingBpmData', 'Bruto BPM ontbreekt in RDW'));
  }
  if (riskFlags?.missingFirstRegDate) {
    missingReasons.push(t('calculator.missingFirstRegDate', 'Eerste registratiedatum ontbreekt'));
  }

  return (
    <div className={cn('rounded-lg border bg-card p-4', className)}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
          <Receipt className="h-4 w-4 text-blue-500" />
        </div>
        <h3 className="font-semibold text-foreground">
          {t('calculator.bpmTitle', 'BPM Berekening')}
        </h3>
      </div>

      {/* Warning banner when BPM is estimated */}
      {isEstimated && !missingData && (
        <div className="flex items-start gap-2 p-2 mb-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-500" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <p className="font-medium">{t('calculator.bpmEstimated', 'BPM geschat')}</p>
            <p className="mt-0.5">
              {t(
                'calculator.bpmEstimatedDescription',
                'Bruto BPM ontbreekt in RDW. Berekend op basis van CO₂ — dit is een indicatie, geen officieel bedrag.'
              )}
            </p>
          </div>
        </div>
      )}

      {missingData ? (
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          <div>
            <p className="font-medium text-foreground">
              {t('calculator.bpmUnavailable', 'BPM niet beschikbaar')}
            </p>
            {missingReasons.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {missingReasons.map((reason, i) => (
                  <li key={i} className="text-xs">
                    {reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Gross BPM */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('calculator.grossBpm', 'Bruto BPM')}
            </span>
            <span className="font-medium">{formatEuro(bpm.grossBpm)}</span>
          </div>

          {/* Depreciation */}
          {bpm.depreciationPct !== null && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {t('calculator.depreciation', 'Afschrijving')}
                {bpm.monthsSinceFirstUse !== null && (
                  <span className="text-xs ml-1">
                    ({bpm.monthsSinceFirstUse} {t('calculator.months', 'mnd')})
                  </span>
                )}
              </span>
              <span className="font-medium">{formatPct(bpm.depreciationPct)}</span>
            </div>
          )}

          {/* Band label */}
          {bpm.bandLabel && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {t('calculator.band', 'Schijf')}
              </span>
              <span className="text-sm text-muted-foreground">{bpm.bandLabel}</span>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-border my-2" />

          {/* Rest BPM - highlighted */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-foreground">
              {t('calculator.restBpm', 'Rest-BPM')}
            </span>
            <span className="font-semibold text-lg text-primary">{formatEuro(bpm.restBpm)}</span>
          </div>

          {/* 5-year rule note */}
          {bpm.cappedBy5YearRule && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('calculator.fiveYearRule', '* 5-jaar regel toegepast (bestelauto)')}
            </p>
          )}

          {/* Electric vehicle note */}
          {isElectric && (
            <p className="text-xs text-green-600 mt-1">
              {t('calculator.electricNoBpm', '✓ Elektrisch voertuig: geen BPM')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
